/**
 * ReceivePurchaseOrderScreen — record goods arriving against an existing PO.
 *
 * - Loads PO by :poId param, guards against already-RECEIVED POs.
 * - Shows each ordered line with its current received_qty and an editable
 *   "receiving now" quantity input (capped at outstanding = ordered − received).
 * - On submit calls receiveAgainstPO which updates stock, supplier balance,
 *   and sets PO status PARTIAL or RECEIVED.
 */

import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Q } from '@nozbe/watermelondb';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { database } from '@/db';
import { PurchaseOrder, PoLineItem, Product } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { formatCurrency } from '@/utils/format';
import { receiveAgainstPO, type ReceivePOLine } from '@/inventory/PurchaseService';

interface EnrichedLine {
  lineItem: PoLineItem;
  product: Product | null;
  receivingNow: string;
}

export function ReceivePurchaseOrderScreen() {
  const { poId } = useParams<{ poId: string }>();
  const navigate = useNavigate();
  const userId = useAppStore((s) => s.currentUser?.id);
  const activeStoreId = useAppStore((s) => s.activeStoreId);

  // Load the PO once on mount.
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [poError, setPoError] = useState<string | null>(null);
  useEffect(() => {
    if (!poId) return;
    database.get<PurchaseOrder>('purchase_orders').find(poId).then(setPurchaseOrder).catch(() => setPoError('PO not found.'));
  }, [poId]);

  // Reactive line items for this PO.
  const lineItems = useObservable<PoLineItem[]>(
    () => poId
      ? database.get<PoLineItem>('po_line_items').query(
        Q.where('purchase_order_id', poId),
        Q.where('is_deleted', false),
      ).observe()
      : null,
    [poId],
    [],
  );

  // Enrich with products (loaded once; products rarely change during a receive flow).
  const [lines, setLines] = useState<EnrichedLine[]>([]);
  useEffect(() => {
    if (lineItems.length === 0) return;
    Promise.all(
      lineItems.map(async (li) => {
        let product: Product | null = null;
        try { product = await database.get<Product>('products').find(li.productId); } catch { /* skip */ }
        const outstanding = li.quantity - li.receivedQty;
        return { lineItem: li, product, receivingNow: outstanding > 0 ? String(outstanding) : '0' };
      }),
    ).then(setLines);
  }, [lineItems]);

  // Supplier invoice
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState('');
  const [supplierInvoiceDate, setSupplierInvoiceDate] = useState('');
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [success, setSuccess] = useState(false);

  if (poError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-body text-fg-secondary">
        <p>{poError}</p>
        <Button variant="secondary" onClick={() => navigate('/purchase-orders')}>Back to PO list</Button>
      </div>
    );
  }

  if (!purchaseOrder) {
    return <div className="text-body text-fg-secondary">Loading…</div>;
  }

  if (success) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-body">
        <CheckCircle2 size={40} className="text-success-ink" />
        <p className="text-heading font-semibold text-fg-primary">Receipt recorded successfully.</p>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate('/purchase-orders')}>Back to PO list</Button>
          <Button onClick={() => navigate('/inventory')}>View inventory</Button>
        </div>
      </div>
    );
  }

  if (purchaseOrder.status === 'RECEIVED') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-body">
        <CheckCircle2 size={40} className="text-success-ink" />
        <p className="font-medium text-fg-primary">PO {purchaseOrder.poNumber} is fully received.</p>
        <Button variant="secondary" onClick={() => navigate('/purchase-orders')}>Back to PO list</Button>
      </div>
    );
  }

  async function handleReceive() {
    if (!purchaseOrder || !userId || !activeStoreId) return;
    const toReceive: ReceivePOLine[] = lines
      .map((l) => ({ lineItem: l.lineItem, product: l.product!, receivedQty: parseFloat(l.receivingNow) || 0 }))
      .filter((l) => l.receivedQty > 0 && l.product != null);
    if (toReceive.length === 0) { setError('Enter at least one quantity to receive.'); return; }
    const overReceive = toReceive.find(
      (l) => l.receivedQty > l.lineItem.quantity - l.lineItem.receivedQty,
    );
    if (overReceive) {
      setError(`Cannot receive more than the ordered quantity for "${overReceive.product?.name ?? 'item'}".`);
      return;
    }
    setSaving(true);
    setError(null);
    const result = await receiveAgainstPO({
      purchaseOrder,
      storeId: activeStoreId,
      createdById: userId,
      lines: toReceive,
      supplierInvoiceNo: supplierInvoiceNo.trim() || null,
      supplierInvoiceDate: supplierInvoiceDate ? new Date(supplierInvoiceDate) : null,
      notes: notes.trim() || null,
    });
    setSaving(false);
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error ?? 'Failed to record receipt.');
    }
  }

  const grandTotal = lines.reduce((sum, l) => {
    const qty = parseFloat(l.receivingNow) || 0;
    return sum + qty * l.lineItem.unitPrice * (1 + l.lineItem.taxRate / 100);
  }, 0);

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Back + heading */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/purchase-orders')}
          className="rounded-md p-1 text-fg-tertiary hover:bg-bg-subtle hover:text-fg-primary transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-display font-semibold text-fg-primary">
            Receive against {purchaseOrder.poNumber}
          </h1>
          <p className="text-body text-fg-secondary">
            Status: <span className="font-medium">{purchaseOrder.status}</span>
            {purchaseOrder.expectedDate && (
              <> · Expected: {purchaseOrder.expectedDate.toLocaleDateString('en-IN')}</>
            )}
          </p>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-1 gap-6 min-h-0 overflow-auto lg:grid-cols-[1fr_300px] lg:overflow-visible">
        {/* Lines */}
        <div className="overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
          <table className="w-full text-body">
            <thead>
              <tr className="border-b border-stroke-hairline/10 text-label uppercase tracking-wide text-fg-tertiary">
                <th className="px-4 py-2.5 text-left">Product</th>
                <th className="px-4 py-2.5 text-right">Ordered</th>
                <th className="px-4 py-2.5 text-right">Received</th>
                <th className="px-4 py-2.5 text-right">Outstanding</th>
                <th className="px-4 py-2.5 text-right">Receiving now</th>
                <th className="px-4 py-2.5 text-right">Unit price</th>
                <th className="px-4 py-2.5 text-right">Line total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const outstanding = l.lineItem.quantity - l.lineItem.receivedQty;
                const qty = parseFloat(l.receivingNow) || 0;
                const lineTotal = qty * l.lineItem.unitPrice * (1 + l.lineItem.taxRate / 100);
                return (
                  <tr key={l.lineItem.id} className="border-b border-stroke-hairline/5">
                    <td className="px-4 py-3 text-fg-primary">
                      {l.product?.name ?? l.lineItem.productId}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-fg-secondary">
                      {l.lineItem.quantity}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-fg-secondary">
                      {l.lineItem.receivedQty}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums font-medium ${outstanding > 0 ? 'text-warning-ink' : 'text-success-ink'}`}>
                      {outstanding}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Input
                        type="number"
                        value={l.receivingNow}
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((x, i) => i === idx ? { ...x, receivingNow: e.target.value } : x),
                          )
                        }
                        className="w-24 text-right"
                        min="0"
                        max={outstanding}
                        disabled={outstanding <= 0}
                      />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-fg-secondary">
                      {formatCurrency(l.lineItem.unitPrice)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-fg-primary">
                      {formatCurrency(lineTotal)}
                    </td>
                  </tr>
                );
              })}
              {lines.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-fg-tertiary">
                    Loading lines…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Side panel: supplier invoice + summary */}
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface p-4 space-y-3">
            <h2 className="text-heading font-semibold text-fg-primary">Supplier invoice</h2>
            <div className="space-y-1">
              <label className="text-label uppercase tracking-wide text-fg-tertiary">Invoice no.</label>
              <Input value={supplierInvoiceNo} onChange={(e) => setSupplierInvoiceNo(e.target.value)} placeholder="Optional" />
            </div>
            <div className="space-y-1">
              <label className="text-label uppercase tracking-wide text-fg-tertiary">Invoice date</label>
              <Input type="date" value={supplierInvoiceDate} onChange={(e) => setSupplierInvoiceDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-label uppercase tracking-wide text-fg-tertiary">Notes</label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
            </div>
          </div>

          <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface p-4 space-y-2">
            <h2 className="text-heading font-semibold text-fg-primary">Receipt total</h2>
            <div className="flex justify-between text-body text-fg-secondary">
              <span>Amount (incl. GST)</span>
              <span className="tabular-nums font-medium text-fg-primary">{formatCurrency(grandTotal)}</span>
            </div>
          </div>


          <Button block disabled={saving} onClick={() => void handleReceive()}>
            {saving ? 'Recording…' : 'Record receipt'}
          </Button>
        </div>
      </div>
    </div>
  );
}
